/**
 * ColorPickerSpectrum  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { CustomPicker, InjectedColorProps } from "react-color";
import { Alpha, Hue, Saturation } from "react-color/lib/components/common";
import { ExportedColorProps } from "react-color/lib/components/common/ColorWrap";
import { BEM } from "@adapptio/lib-components";
import { ColorPickerControls } from "./ColorPickerControls";
import { ColorPickerHandle } from "./ColorPickerHandle";

export interface IColorPickerSpectrumBaseComponentProps extends InjectedColorProps {
	className?: string;
}

export const ColorPickerSpectrumBaseComponent: React.FC<IColorPickerSpectrumBaseComponentProps> = ({
	className,
	onChange,
	...props
}) => {
	const baseClass = BEM("edt-color-picker-spectrum");

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("saturation").toClassName()}>
				<Saturation onChange={onChange} {...props} />
			</div>
			<div className={baseClass.element("hue").toClassName()}>
				<Hue onChange={onChange} pointer={ColorPickerHandle} {...props} />
			</div>
			<div className={baseClass.element("alpha").toClassName()}>
				<Alpha onChange={onChange} pointer={ColorPickerHandle} {...props} />
			</div>
			<div className={baseClass.element("controls").toClassName()}>
				<ColorPickerControls color={props.rgb} onChange={onChange} />
			</div>
		</div>
	);
};

export interface IColoColorPickerSpectrumBaseProps extends ExportedColorProps {
	className?: string;
}

export const ColorPickerSpectrumBase: React.ComponentClass<IColoColorPickerSpectrumBaseProps> = CustomPicker(
	ColorPickerSpectrumBaseComponent
);
